import React, { useState } from 'react';
import { Save } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import config from '../../config';
import axios from "axios";

const CategoryAdd = () => {
  const navigate = useNavigate();

  const [activeLanguage, setActiveLanguage] = useState('en');

  // English (default) form data
  const [formData, setFormData] = useState({
    language: 'en',
    name: '',
    description: ''
  });

  // Spanish translation form data
  const [spanishData, setSpanishData] = useState({
    language: 'es',
    name: '',
    description: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Generic input change handler
  const handleInputChange = (field, value) => {
   
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear any existing error for this field
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  // Spanish input change handler
  const handleSpanishInputChange = (field, value) => {
    setSpanishData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Form validation
  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Category name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Submit form
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        language: formData.language,
        name: formData.name.trim(),
        description: formData.description.trim(),
        spanishData: JSON.stringify({
          language: 'es',
          name: spanishData.name?.trim() || '',
          description: spanishData.description?.trim() || ''
        })
      };

      const response = await axios.post(`${config.BASE_URL}/api/admin/categories`, payload, {
        withCredentials: true,
        headers: {
          "Content-Type": "application/json",
        }
      });


      if (response.status === 200 || response.status === 201) {
        setSuccessMessage('Category added successfully!');
        setTimeout(() => {
          const token = localStorage.getItem("token");
          if (token) {
            navigate("/admin/category");
          } else {
            navigate("/admin/login");
          }
        }, 1000);
      }
    } catch (err) {
      console.error("Error adding category:", err);

      let errorMessage = "Failed to add category";

      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response?.status === 400) {
        errorMessage = "Invalid category data provided";
      } else if (err.response?.status === 409) {
        errorMessage = "Category name already exists";
      } else if (err.response?.status === 401) {
        errorMessage = "Unauthorized. Please login again.";
        navigate("/admin/login");
        return;
      }

      setErrors({ submit: errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackButton = () => {
    navigate(-1);
  };

  return (
    <div className="dashboard">
      <div className="container">
        <h1 className="dash_title">Dashboard</h1>
        <div className="main-section">
          <div className="back_btn_wrap">
            <button className="back-btn" onClick={handleBackButton}>Go Back</button>
          </div>
          <div className="main-inner add_project_main">
            {/* Language Tabs */}
            <div className="language-tabs" style={{ marginBottom: '20px' }}>
              <button
                type="button"
                className={`tab-btn ${activeLanguage === 'en' ? 'active' : ''}`}
                onClick={() => setActiveLanguage('en')}
                style={{
                  padding: '10px 20px',
                  marginRight: '10px',
                  backgroundColor: activeLanguage === 'en' ? '#007bff' : '#f8f9fa',
                  color: activeLanguage === 'en' ? 'white' : 'black',
                  border: '1px solid #ddd',
                  cursor: 'pointer'
                }}
              >
                English
              </button>
              <button
                type="button"
                className={`tab-btn ${activeLanguage === 'es' ? 'active' : ''}`}
                onClick={() => setActiveLanguage('es')}
                style={{
                  padding: '10px 20px',
                  backgroundColor: activeLanguage === 'es' ? '#007bff' : '#f8f9fa',
                  color: activeLanguage === 'es' ? 'white' : 'black',
                  border: '1px solid #ddd',
                  cursor: 'pointer'
                }}
              >
                Spanish
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="language-content">
                {/* English Content */}
                {activeLanguage === 'en' && (
                  <div className="language-content">
                    <h3>English Content</h3>
                    <div className="add_field titleField">
                      <h2>Category Name (English) *</h2>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={(e) => handleInputChange('name', e.target.value)}
                        placeholder="Enter category name"
                        required
                        disabled={isSubmitting}
                      />
                      {errors.name && (
                        <p className="error-text">{errors.name}</p>
                      )}
                    </div>
                    <div className="add_field textareaField">
                      <h2>Description (English)</h2>
                      <textarea
                        name="description"
                        value={formData.description}
                        onChange={(e) => handleInputChange('description', e.target.value)}
                        rows="6"
                        placeholder="Enter category description (optional)"
                        disabled={isSubmitting}
                      />
                    </div>
                  </div>
                )}

                {/* Spanish Content */}
                {activeLanguage === 'es' && (
                  <div className="language-content">
                    <h3>Spanish Content</h3>
                    <div className="add_field titleField">
                      <h2>Category Name (Spanish)</h2>
                      <input
                        type="text"
                        name="name"
                        value={spanishData.name}
                        onChange={(e) => handleSpanishInputChange('name', e.target.value)}
                        placeholder="Ingrese el nombre de la categoría"
                        disabled={isSubmitting}
                      />
                    </div>
                    <div className="add_field textareaField">
                      <h2>Description (Spanish)</h2>
                      <textarea
                        name="description"
                        value={spanishData.description}
                        onChange={(e) => handleSpanishInputChange('description', e.target.value)}
                        rows="6"
                        placeholder="Ingrese la descripción de la categoría (opcional)"
                        disabled={isSubmitting}
                      />
                    </div>
                  </div>
                )}
              </div>

              <div className="add_project">
                <button type="submit" disabled={isSubmitting}>
                  <Save className="w-5 h-5" />
                  {isSubmitting ? 'Adding Category...' : 'Add Category'}
                </button>
              </div>
            </form>

            {successMessage && (
              <p className="success-message">{successMessage}</p>
            )}

            {errors.submit && (
              <p className="error-message">{errors.submit}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CategoryAdd;